"use client"

import { Trans } from "@lingui/react/macro"
import { NavigationTrigger, cn } from "@rarible_int/anaconda_ui_components"
import Link from "next/link"
import { notFound, usePathname } from "next/navigation"
import { getRoutableBlockchains } from "../blockchains/supported-blockchains"
import { ProfileLinkWrapper } from "../components/profile-link"

function extractBlockchainFromPath(pathname: string): string | null {
  const paths = pathname.split("/").filter(Boolean)
  return paths.length > 0 ? paths[0] : null
}

function isBlockchainSlug(slug: string): boolean {
  const upperSlug = slug.toUpperCase()
  const supportedBlockchains = getRoutableBlockchains().map((b) =>
    b.toUpperCase(),
  )
  return upperSlug === "ALL" || supportedBlockchains.includes(upperSlug)
}

function isGachaPath(pathname: string): boolean {
  return pathname === "/gacha" || pathname.startsWith("/gacha/")
}

export function MarketplaceNavLabel() {
  return <Trans>Marketplace</Trans>
}

export function MarketplaceLink() {
  return (
    <Link href="/">
      <NavigationTrigger
        active={false}
        className="text-foreground/100 hover:text-foreground/100"
      >
        <MarketplaceNavLabel />
      </NavigationTrigger>
    </Link>
  )
}

export function ExploreLink() {
  const pathname = usePathname()
  if (isGachaPath(pathname)) return null

  const firstSegment = extractBlockchainFromPath(pathname)

  // Only use blockchain-specific link if we're actually on a blockchain route
  // For brand pages and other routes, always link to the main explore page
  const href =
    firstSegment && isBlockchainSlug(firstSegment) ? `/${firstSegment}` : "/all"

  return (
    <Link href={href}>
      <NavigationTrigger active={false}>
        <Trans>Explore</Trans>
      </NavigationTrigger>
    </Link>
  )
}

export function MintLink() {
  const pathname = usePathname()
  if (isGachaPath(pathname)) return null

  const blockchain = extractBlockchainFromPath(pathname)

  // For blockchain-prefixed routes, link to that blockchain's drops page
  const href = blockchain ? `/${blockchain}/drops` : "/drops"
  const isActive = pathname.startsWith(href)

  return (
    <Link href={href} prefetch={false}>
      <NavigationTrigger active={isActive}>
        <Trans>Mint</Trans>
      </NavigationTrigger>
    </Link>
  )
}

export const ProfileLink = ProfileLinkWrapper

export function SwapLink() {
  const pathname = usePathname()
  if (isGachaPath(pathname)) return null

  const blockchain = extractBlockchainFromPath(pathname)

  const href = blockchain ? `/${blockchain}/swap` : "/swap"
  const isActive = pathname.startsWith(href)

  return (
    <Link href={href}>
      <NavigationTrigger active={isActive}>
        <Trans>Swap</Trans>
      </NavigationTrigger>
    </Link>
  )
}

// The marketplace header sends Gacha visitors to the RWA Marketplace's gacha.
export const RWA_GACHA_URL = "https://rwa.rarible.com/gacha"

export function GachaLink() {
  const pathname = usePathname()
  if (isGachaPath(pathname)) return null

  return (
    <a href={RWA_GACHA_URL}>
      <NavigationTrigger active={false}>
        <Trans>Gacha</Trans>
      </NavigationTrigger>
    </a>
  )
}

const COLLECTIBLES_URL = "https://rwa.rarible.com"

export function CollectiblesLink() {
  const pathname = usePathname()
  if (isGachaPath(pathname)) return null

  return (
    <a href={COLLECTIBLES_URL}>
      <NavigationTrigger active={false} className="flex items-center gap-2">
        <Trans>Collectibles</Trans>
        <span className="rounded-[2px] bg-brand px-1.5 py-0.5 font-bold text-[11px] text-black leading-none">
          <Trans>NEW</Trans>
        </span>
      </NavigationTrigger>
    </a>
  )
}

export function GachaNavigationLinks() {
  const pathname = usePathname()
  if (!isGachaPath(pathname)) return null

  return (
    <>
      <Link href="/gacha">
        <NavigationTrigger active={pathname.startsWith("/gacha")}>
          <Trans>Gacha</Trans>
        </NavigationTrigger>
      </Link>
      <MarketplaceLink />
    </>
  )
}

export function CreateLink() {
  return (
    <a
      href="https://og.rarible.com/create/start"
      target="_blank"
      rel="noreferrer"
    >
      <NavigationTrigger active={false}>
        <Trans>Create</Trans>
      </NavigationTrigger>
    </a>
  )
}
